import React, { memo, useEffect, useState } from 'react';
import { findIndex, cloneDeep } from 'lodash';
import clsx from 'clsx';
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS as cssDndKit } from '@dnd-kit/utilities';
import { Reorder as ReorderIcon } from '@mui/icons-material';
import PropTypes from 'prop-types';

import Button from '../../../../common/components/Button/Button';
import { Dialog } from '../../../../common/components/Dialog/Dialog';
import Toggle from '../../../../common/components/Toggle/Toggle';
import { isMobile } from '../../../../common/services/isMobile';
import { arrayMove } from '../../../../common/services/ArrayUtils';

function DragHandle() {
  return (
    <div className="reorder-container">
      <ReorderIcon className="reorder" />
    </div>
  );
}

function SortableItem({ value, visibilityToggle, isMobile, id, activeId }) {
  const { setNodeRef, transform, transition, listeners } = useSortable({ id });

  const style = {
    transform: cssDndKit.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...listeners}
      className={clsx('SortableItem', {
        hidden: value.show === false,
        draggable: id === activeId,
      })}
    >
      {isMobile && <DragHandle />}
      <span>{value.label}</span>
      <Toggle toggled={value.show} onToggle={() => visibilityToggle(value.id)} color="primary" />
    </li>
  );
}

export function SortableList({ items, visibilityToggle, isMobile, onSortEnd }) {
  const [activeId, setActiveId] = useState(null);
  const getIndex = (id) => findIndex(items, { id });
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
    }),
  );

  return (
    <DndContext
      sensors={sensors}
      autoScroll={false}
      onDragStart={({ active }) => {
        if (active) {
          setActiveId(active.id);
        }
      }}
      onDragEnd={({ active, over }) => {
        if (over && active.id !== over.id) {
          onSortEnd({
            oldIndex: getIndex(active.id),
            newIndex: getIndex(over.id),
          });
        }
        setActiveId(null);
      }}
      onDragCancel={() => setActiveId(null)}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <ul className="SortableList">
          {items.map((value, index) => (
            <SortableItem
              id={value.id}
              activeId={activeId}
              key={`item-${value.id}`}
              index={index}
              value={value}
              visibilityToggle={visibilityToggle}
              isMobile={isMobile}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

const PersonalizeModal = memo(({ columnsConfig, isOpen, onClose, onSubmit }) => {
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(cloneDeep(columnsConfig));
  }, [isOpen, columnsConfig]);

  const visibilityToggle = (id) => {
    const newItems = items.map((item) => {
      const itemCopy = { ...item };

      if (itemCopy.id === id) itemCopy.show = !itemCopy.show;

      return itemCopy;
    });

    setItems(newItems);
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    setItems(arrayMove(items, oldIndex, newIndex));
  };

  const actions = [
    <Button key="cancel-btn" color="secondary" onClick={onClose}>
      Cancel
    </Button>,
    <Button key="submit-btn" color="primary" onClick={() => onSubmit(items)}>
      Submit
    </Button>,
  ];

  return (
    <Dialog title="Personalize Сolumns" modal={false} actions={actions} open={isOpen} onClose={onClose} fullWidth>
      <SortableList
        helperClass="SortableHelper"
        items={items}
        visibilityToggle={(id) => visibilityToggle(id)}
        onSortEnd={onSortEnd}
        isMobile={isMobile}
      />
    </Dialog>
  );
});

PersonalizeModal.propTypes = {
  isOpen: PropTypes.bool,
  columnsConfig: PropTypes.array,
};

export default PersonalizeModal;
